// ==============================================================================
// UTILITÁRIOS DE HORÁRIOS DE FUNCIONAMENTO SEMANAIS (SALÕES, CLÍNICAS E BELEZA)
// Suporte a horários diferenciados (dias de semana vs sábados/finais de semana)
// ==============================================================================

export type DayKey = 'dom' | 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab';

export interface DaySchedule {
  isOpen: boolean;
  start: string; // "09:00"
  end: string;   // "19:00"
}

export interface WeeklyBusinessHours {
  version: 2;
  summary: string;
  days: Record<DayKey, DaySchedule>;
}

export const DAY_KEYS_ORDERED: DayKey[] = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

export const DAY_LABELS: Record<DayKey, { name: string; short: string }> = {
  seg: { name: 'Segunda-feira', short: 'Seg' },
  ter: { name: 'Terça-feira', short: 'Ter' },
  qua: { name: 'Quarta-feira', short: 'Qua' },
  qui: { name: 'Quinta-feira', short: 'Qui' },
  sex: { name: 'Sexta-feira', short: 'Sex' },
  sab: { name: 'Sábado', short: 'Sáb' },
  dom: { name: 'Domingo', short: 'Dom' }
};

export const DEFAULT_SALON_HOURS: WeeklyBusinessHours = {
  version: 2,
  summary: 'Terça a Sexta: 09:00 às 19:00 • Sábado: 08:00 às 17:00 • Dom e Seg: Fechado',
  days: {
    seg: { isOpen: false, start: '09:00', end: '19:00' },
    ter: { isOpen: true, start: '09:00', end: '19:00' },
    qua: { isOpen: true, start: '09:00', end: '19:00' },
    qui: { isOpen: true, start: '09:00', end: '19:00' },
    sex: { isOpen: true, start: '09:00', end: '19:00' },
    sab: { isOpen: true, start: '08:00', end: '17:00' },
    dom: { isOpen: false, start: '08:00', end: '14:00' }
  }
};

/**
 * Converte string de hora 'HH:MM' para minutos desde 00:00
 */
export function timeStringToMinutes(timeStr: string): number {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(n => parseInt(n, 10) || 0);
  return h * 60 + m;
}

/**
 * Converte minutos desde 00:00 para string 'HH:MM'
 */
export function minutesToTimeString(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Retorna o DayKey correspondente a uma data YYYY-MM-DD ou Date
 */
export function getDayKeyFromDate(dateOrStr: string | Date): DayKey {
  let d: Date;
  if (typeof dateOrStr === 'string') {
    const [y, m, day] = dateOrStr.split('-').map(Number);
    d = new Date(y, m - 1, day, 12, 0, 0);
  } else {
    d = dateOrStr;
  }
  const dayIndex = d.getDay(); // 0 = Domingo, 1 = Segunda, ... 6 = Sábado
  const map: DayKey[] = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];
  return map[dayIndex];
}

/**
 * Sintetiza um resumo textual limpo e humanizado dos horários
 * Ex: "Ter a Sex: 09:00 às 19:00 • Sáb: 08:00 às 17:00"
 */
export function generateHumanSummary(days: Record<DayKey, DaySchedule>): string {
  const openDays = DAY_KEYS_ORDERED.filter(k => days[k]?.isOpen);
  if (openDays.length === 0) {
    return 'Fechado temporariamente';
  }

  // Agrupa dias consecutivos com mesmo start e end
  const groups: Array<{ keys: DayKey[]; start: string; end: string }> = [];
  let currentGroup: { keys: DayKey[]; start: string; end: string } | null = null;

  for (const k of DAY_KEYS_ORDERED) {
    const sched = days[k];
    if (!sched || !sched.isOpen) {
      if (currentGroup) {
        groups.push(currentGroup);
        currentGroup = null;
      }
      continue;
    }

    if (
      currentGroup &&
      currentGroup.start === sched.start &&
      currentGroup.end === sched.end
    ) {
      currentGroup.keys.push(k);
    } else {
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentGroup = { keys: [k], start: sched.start, end: sched.end };
    }
  }
  if (currentGroup) {
    groups.push(currentGroup);
  }

  const parts = groups.map(g => {
    let dayRangeStr = '';
    if (g.keys.length === 1) {
      dayRangeStr = DAY_LABELS[g.keys[0]].short;
    } else if (g.keys.length === 2) {
      dayRangeStr = `${DAY_LABELS[g.keys[0]].short} e ${DAY_LABELS[g.keys[1]].short}`;
    } else {
      dayRangeStr = `${DAY_LABELS[g.keys[0]].short} a ${DAY_LABELS[g.keys[g.keys.length - 1]].short}`;
    }
    return `${dayRangeStr}: ${g.start} às ${g.end}`;
  });

  return parts.join(' • ');
}

/**
 * Converte texto legado como "Terça a Sábado: 08:00 às 19:00" em objeto estruturado
 */
export function parseLegacyStringToSchedule(text: string): WeeklyBusinessHours {
  const defaultDays: Record<DayKey, DaySchedule> = {
    seg: { isOpen: false, start: '08:00', end: '19:00' },
    ter: { isOpen: true, start: '08:00', end: '19:00' },
    qua: { isOpen: true, start: '08:00', end: '19:00' },
    qui: { isOpen: true, start: '08:00', end: '19:00' },
    sex: { isOpen: true, start: '08:00', end: '19:00' },
    sab: { isOpen: true, start: '08:00', end: '19:00' },
    dom: { isOpen: false, start: '08:00', end: '14:00' }
  };

  const lower = (text || '').toLowerCase();

  // Tenta extrair horários "HH:MM às HH:MM" ou "HH:MM a HH:MM"
  const timeMatch = lower.match(/(\d{1,2}:\d{2})\s*(?:às|as|a|-)\s*(\d{1,2}:\d{2})/);
  const startTime = timeMatch ? timeMatch[1].padStart(5, '0') : '08:00';
  const endTime = timeMatch ? timeMatch[2].padStart(5, '0') : '19:00';

  // Identifica dias inclusos
  const includesSeg = lower.includes('segunda') || lower.includes('seg');
  const includesSab = lower.includes('sábado') || lower.includes('sabado') || lower.includes('sáb') || lower.includes('sab');
  const includesDom = lower.includes('domingo') || lower.includes('dom');

  defaultDays.seg.isOpen = includesSeg;
  defaultDays.seg.start = startTime;
  defaultDays.seg.end = endTime;

  defaultDays.ter.isOpen = true;
  defaultDays.ter.start = startTime;
  defaultDays.ter.end = endTime;

  defaultDays.qua.isOpen = true;
  defaultDays.qua.start = startTime;
  defaultDays.qua.end = endTime;

  defaultDays.qui.isOpen = true;
  defaultDays.qui.start = startTime;
  defaultDays.qui.end = endTime;

  defaultDays.sex.isOpen = true;
  defaultDays.sex.start = startTime;
  defaultDays.sex.end = endTime;

  defaultDays.sab.isOpen = includesSab;
  defaultDays.sab.start = startTime;
  defaultDays.sab.end = endTime;

  defaultDays.dom.isOpen = includesDom;
  defaultDays.dom.start = startTime;
  defaultDays.dom.end = endTime;

  return {
    version: 2,
    summary: text.trim() || generateHumanSummary(defaultDays),
    days: defaultDays
  };
}

/**
 * Faz o parse seguro do campo businessHours (seja JSON estruturado ou string legada)
 */
export function parseBusinessHours(raw?: string | null): WeeklyBusinessHours {
  if (!raw || !raw.trim()) {
    return DEFAULT_SALON_HOURS;
  }

  const trimmed = raw.trim();

  // Tenta fazer parse como JSON se começar com '{'
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && parsed.days && typeof parsed.days === 'object') {
        const validatedDays: Record<DayKey, DaySchedule> = {
          seg: parsed.days.seg || { isOpen: false, start: '09:00', end: '19:00' },
          ter: parsed.days.ter || { isOpen: true, start: '09:00', end: '19:00' },
          qua: parsed.days.qua || { isOpen: true, start: '09:00', end: '19:00' },
          qui: parsed.days.qui || { isOpen: true, start: '09:00', end: '19:00' },
          sex: parsed.days.sex || { isOpen: true, start: '09:00', end: '19:00' },
          sab: parsed.days.sab || { isOpen: true, start: '08:00', end: '17:00' },
          dom: parsed.days.dom || { isOpen: false, start: '08:00', end: '14:00' }
        };
        return {
          version: 2,
          summary: parsed.summary || generateHumanSummary(validatedDays),
          days: validatedDays
        };
      }
    } catch {
      // Falha no JSON, cai no fallback de string legada
    }
  }

  return parseLegacyStringToSchedule(trimmed);
}

/**
 * Serializa a programação para salvar no banco mantendo o summary atualizado
 */
export function serializeBusinessHours(schedule: WeeklyBusinessHours): string {
  const summary = generateHumanSummary(schedule.days);
  const data: WeeklyBusinessHours = {
    version: 2,
    summary,
    days: schedule.days
  };
  return JSON.stringify(data);
}

/**
 * Obtém a programação (se abre e horários) de uma data específica
 */
export function getDaySchedule(
  scheduleOrRaw: WeeklyBusinessHours | string | null | undefined,
  dateOrDay: string | Date | DayKey
): DaySchedule {
  const schedule = (!scheduleOrRaw || typeof scheduleOrRaw === 'string')
    ? parseBusinessHours(scheduleOrRaw as string | null | undefined)
    : scheduleOrRaw;

  let key: DayKey;
  if (typeof dateOrDay === 'string' && dateOrDay in schedule.days) {
    key = dateOrDay as DayKey;
  } else {
    key = getDayKeyFromDate(dateOrDay as string | Date);
  }

  return schedule.days[key] || { isOpen: false, start: '09:00', end: '19:00' };
}

/**
 * Gera a lista de slots em intervalos (ex: 30 minutos) para um dia específico
 * Se o salão estiver fechado, retorna array vazio [].
 */
export function generateDaySlots(
  scheduleOrRaw: WeeklyBusinessHours | string | null | undefined,
  dateOrDay: string | Date | DayKey,
  intervalMinutes: number = 30
): string[] {
  const daySched = getDaySchedule(scheduleOrRaw, dateOrDay);
  if (!daySched.isOpen) {
    return [];
  }

  const startMin = timeStringToMinutes(daySched.start);
  const endMin = timeStringToMinutes(daySched.end);

  if (startMin >= endMin) {
    return [];
  }

  const slots: string[] = [];
  for (let m = startMin; m < endMin; m += intervalMinutes) {
    slots.push(minutesToTimeString(m));
  }

  return slots;
}

/**
 * Gera slots dinâmicos combinando a grade base com o término exato de cada atendimento do dia,
 * garantindo que o próximo horário vago fique imediatamente disponível para agendamento.
 */
export function generateDynamicDaySlots(
  scheduleOrRaw: WeeklyBusinessHours | string | null | undefined,
  dateOrDay: string | Date | DayKey,
  appointments: Array<{
    scheduledDate?: string;
    scheduledTime?: string;
    durationMinutes?: number;
    status?: string;
    allDay?: boolean;
  }> = [],
  baseIntervalMinutes: number = 30
): string[] {
  const daySched = getDaySchedule(scheduleOrRaw, dateOrDay);
  if (!daySched.isOpen) {
    return [];
  }

  const startMin = timeStringToMinutes(daySched.start);
  const endMin = timeStringToMinutes(daySched.end);
  if (startMin >= endMin) {
    return [];
  }

  const baseSlots = generateDaySlots(scheduleOrRaw, dateOrDay, baseIntervalMinutes);

  const dateStr = typeof dateOrDay === 'string' && dateOrDay.includes('-')
    ? dateOrDay
    : (dateOrDay instanceof Date ? dateOrDay.toISOString().split('T')[0] : '');

  const endSlots: string[] = [];
  const startSlots: string[] = [];

  for (const a of appointments) {
    if (dateStr && a.scheduledDate && a.scheduledDate !== dateStr) continue;
    if (a.status === 'canceled') continue;
    if (!a.scheduledTime) continue;

    const aStart = timeStringToMinutes(a.scheduledTime);
    if (aStart >= startMin && aStart < endMin) {
      startSlots.push(minutesToTimeString(aStart));
    }

    if (!a.allDay && (!a.durationMinutes || a.durationMinutes < 720)) {
      const aEnd = aStart + (a.durationMinutes || 30);
      if (aEnd >= startMin && aEnd < endMin) {
        endSlots.push(minutesToTimeString(aEnd));
      }
    }
  }

  return Array.from(new Set([...baseSlots, ...endSlots, ...startSlots])).filter(Boolean).sort();
}

